// sound.js
//
// TikTok-style "Sound" page for Moment.js.
//
// Two modes, one component:
//
//   mode="detail"  Opened when the user taps the sound row / spinning disc
//                  on a Moment. Shows the sound (cover disc, title, artist,
//                  duration), lets the user preview it (NOT auto-played —
//                  the user taps the disc), lists the videos that use it,
//                  and has a "Use this sound" button that shows a
//                  Photo / Video chooser right on this page.
//
//   mode="pick"    Opened from the post preview sheet ("Add sound").
//                  Lists every sound found in the loaded posts (most used
//                  first), with search, preview and a "Use" button.
//
// Sounds are NOT stored as a separate collection — they live on the post as
// `post.sound` (same shape the server sanitises in mediaSocket.js):
//
//   { soundId, title, artist, url, duration, startTime, isOriginal }
//
// A post with no `sound` is treated as having an "Original Sound" — the
// audio of its own first video. Anyone can "use" that sound too: the new
// post then carries { url: <source video url>, isOriginal: false,
// title: "Original Sound", artist: <source author> }.
//
// Usage (see Moment.js):
//
//   import { Sound, getPostSound, formatSoundLabel, toSoundPayload } from "./sound";
//
//   <Sound
//     sound={getPostSound(post)}      // omit for mode="pick"
//     mode="detail"
//     posts={allLoadedPosts}
//     onBack={() => setSoundPage(null)}
//     onUseSound={(sound, kind) => ...}   // kind: "photo" | "video" | undefined
//     onSelectPost={(post) => ...}
//   />

import React, { useEffect, useMemo, useRef, useState } from "react";
import { MusicIcon } from "../components/mediaIcon";

/* =========================================================
   🔧 HELPERS
========================================================= */

const formatCount = (value) => {
  const num = Number(value) || 0;
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(num % 1_000_000 === 0 ? 0 : 1)}M`;
  }
  if (num >= 1_000) {
    return `${(num / 1_000).toFixed(num % 1_000 === 0 ? 0 : 1)}K`;
  }
  return `${num}`;
};

const formatDuration = (sec = 0) => {
  const t = Math.max(0, Math.floor(Number(sec) || 0));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
};

const mediaItemsOf = (post) =>
  Array.isArray(post?.media) ? post.media : post?.media ? [post.media] : [];

/** Identity of a sound: library id if it has one, otherwise its audio url. */
export const getSoundKey = (sound) => sound?.soundId || sound?.url || null;

/** "Title - Artist" / "Original Sound - username" */
export function formatSoundLabel(sound) {
  if (!sound) return "";
  const artist = sound.artist ? ` - ${sound.artist}` : "";
  if (sound.isOriginal) return `Original Sound${artist}`;
  return `${sound.title || "Original Sound"}${artist}`;
}

/**
 * The sound a post plays, normalised.
 *  - post.sound set (not original)  -> that sound
 *  - otherwise                       -> the post's own video audio
 */
export function getPostSound(post) {
  if (!post) return null;

  const s = post.sound || null;
  const username = post.author?.username || post.authorUsername || "unknown";

  if (s && !s.isOriginal && (s.url || s.title)) {
    return {
      soundId: s.soundId || null,
      title: s.title || "Original Sound",
      artist: s.artist || null,
      url: s.url || null,
      duration: Number(s.duration) || 0,
      startTime: Number(s.startTime) || 0,
      isOriginal: false,
      sourcePostId: null,
    };
  }

  const video = mediaItemsOf(post).find((m) => m?.type === "video");
  const url = s?.url || video?.url || null;

  return {
    soundId: s?.soundId || (url ? `original:${post.postId}` : null),
    title: "Original Sound",
    artist: s?.artist || username,
    url,
    duration: Number(s?.duration) || Number(video?.duration) || 0,
    startTime: Number(s?.startTime) || 0,
    isOriginal: true,
    sourcePostId: post.postId || null,
  };
}

/**
 * The object to put on the new post (`sound` field of mediaCreatePost).
 * Matches the server's soundSchema. Returns null if the sound has no audio.
 */
export function toSoundPayload(sound) {
  if (!sound?.url) return null;
  return {
    soundId: sound.soundId || null,
    title: sound.isOriginal ? "Original Sound" : sound.title || "Original Sound",
    artist: sound.artist || null,
    url: sound.url,
    duration: Number(sound.duration) || 0,
    startTime: 0,
    isOriginal: false, // a post that *uses* a sound never owns it
  };
}

/** Posts (from `posts`) that play the given sound. */
export function postsUsingSound(posts = [], sound) {
  const key = getSoundKey(sound);
  if (!key) return [];
  return posts.filter((p) => getSoundKey(getPostSound(p)) === key);
}

/** Unique sounds across posts, most used first (then most recent). */
export function collectSounds(posts = []) {
  const map = new Map();

  posts.forEach((p) => {
    const s = getPostSound(p);
    const key = getSoundKey(s);
    if (!key || !s.url) return;

    const ts = Date.parse(p.createdAt) || 0;
    const entry = map.get(key);

    if (entry) {
      entry.count += 1;
      entry.latest = Math.max(entry.latest, ts);
      if (s.isOriginal) entry.sound = s; // keep the owner's version
    } else {
      map.set(key, { key, sound: s, count: 1, latest: ts });
    }
  });

  return [...map.values()]
    .sort((a, b) => b.count - a.count || b.latest - a.latest)
    .slice(0, 100);
}

/* =========================================================
   🎨 ICONS
========================================================= */

function BackIcon({ size = 22, color = "#fff" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M15 5 8 12l7 7"
        stroke={color}
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PlayIcon({ size = 20, color = "#fff" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function PauseIcon({ size = 20, color = "#fff" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
      <rect x="6" y="5" width="4" height="14" rx="1" />
      <rect x="14" y="5" width="4" height="14" rx="1" />
    </svg>
  );
}

function PhotoGlyph({ size = 20, color = "#fff" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="3" y="4" width="18" height="16" rx="2.5" stroke={color} strokeWidth="1.7" />
      <circle cx="8.5" cy="9.5" r="1.5" fill={color} />
      <path
        d="M4 17.5 9 12l3.5 3.5L15 13l5 5"
        stroke={color}
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function VideoGlyph({ size = 20, color = "#fff" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="3" y="6" width="13" height="12" rx="2.5" stroke={color} strokeWidth="1.7" />
      <path
        d="m16.5 10.3 4-2.3v8l-4-2.3"
        stroke={color}
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SearchIcon({ size = 16, color = "#9a9a9a" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="11" cy="11" r="6.5" stroke={color} strokeWidth="2" />
      <path d="m16 16 4.5 4.5" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/* =========================================================
   🎵 SOUND PAGE
========================================================= */

export function Sound({
  sound = null,
  mode = "detail",
  posts = [],
  onBack,
  onUseSound,
  onSelectPost,
  // true => covers the whole viewport (used when opened from the preview
  // sheet, which is itself position:fixed). false => fills the Moment screen.
  fixed = false,
}) {
  const isPick = mode === "pick" || !sound;

  const audioRef = useRef(null);
  const [playingKey, setPlayingKey] = useState(null);
  const [query, setQuery] = useState("");
  // Sound waiting for the user to choose Photo / Video (detail mode only).
  const [chooserSound, setChooserSound] = useState(null);

  /* ---------- preview audio ---------- */

  const playSound = (snd) => {
    const a = audioRef.current;
    if (!a || !snd?.url) return;

    a.src = snd.url;
    a.onloadedmetadata = () => {
      if (snd.startTime) a.currentTime = snd.startTime;
    };

    const p = a.play();
    setPlayingKey(getSoundKey(snd));
    if (p && typeof p.catch === "function") {
      p.catch(() => setPlayingKey(null));
    }
  };

  const togglePreview = (snd) => {
    const a = audioRef.current;
    if (!a || !snd?.url) return;

    if (playingKey === getSoundKey(snd) && !a.paused) {
      a.pause();
      setPlayingKey(null);
      return;
    }
    playSound(snd);
  };

  // The page does NOT auto-play: the user taps the disc / play button to
  // preview. Everything stops when the page closes.
  useEffect(() => {
    return () => {
      const a = audioRef.current;
      if (a) a.pause();
    };
  }, []);

  const handleBack = () => {
    audioRef.current?.pause();
    onBack?.();
  };

  // Picker rows already have their files chosen -> attach straight away.
  // Detail page -> ask Photo or Video first, right here on the Sound page.
  const handleUse = (snd) => {
    audioRef.current?.pause();
    setPlayingKey(null);
    if (isPick) {
      onUseSound?.(snd);
    } else {
      setChooserSound(snd);
    }
  };

  const handleChooseKind = (kind) => {
    const snd = chooserSound;
    setChooserSound(null);
    if (snd) onUseSound?.(snd, kind);
  };

  const handleSelectPost = (post) => {
    audioRef.current?.pause();
    onSelectPost?.(post);
  };

  /* ---------- data ---------- */

  const usingPosts = useMemo(
    () => (isPick ? [] : postsUsingSound(posts, sound)),
    [isPick, posts, sound]
  );

  const allSounds = useMemo(
    () => (isPick ? collectSounds(posts) : []),
    [isPick, posts]
  );

  const filteredSounds = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allSounds;
    return allSounds.filter(({ sound: s }) =>
      `${s.title || ""} ${s.artist || ""}`.toLowerCase().includes(q)
    );
  }, [allSounds, query]);

  /* ---------- render ---------- */

  const rootStyle = {
    ...S.root,
    ...(fixed ? S.rootFixed : S.rootAbsolute),
  };

  // Keep wheel / swipe / arrow keys from reaching Moment's post navigation
  // (they would otherwise change the post behind this page).
  const stop = (e) => e.stopPropagation();

  return (
    <div
      style={rootStyle}
      onWheel={stop}
      onTouchStart={stop}
      onTouchEnd={stop}
      onKeyDown={stop}
    >
      <div style={S.column}>
        {/* Header */}
        <div style={S.header}>
          <button
            type="button"
            onClick={handleBack}
            style={S.iconButton}
            aria-label="Back"
          >
            <BackIcon />
          </button>
          <div style={S.headerTitle}>{isPick ? "Choose a sound" : "Sound"}</div>
          <div style={S.headerSpacer} />
        </div>

        {isPick ? (
          /* =================== PICK MODE =================== */
          <>
            <div style={S.searchWrap}>
              <SearchIcon />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search sounds"
                style={S.searchInput}
              />
            </div>

            <div style={S.scroll}>
              {filteredSounds.length ? (
                filteredSounds.map(({ key, sound: s, count }) => {
                  const playing = playingKey === key;
                  return (
                    <div key={key} style={S.row}>
                      <button
                        type="button"
                        onClick={() => togglePreview(s)}
                        style={S.rowPlay}
                        aria-label={playing ? "Pause preview" : "Play preview"}
                      >
                        {playing ? <PauseIcon size={18} /> : <PlayIcon size={18} />}
                      </button>

                      <div style={S.rowInfo}>
                        <div style={S.rowTitle}>
                          {s.isOriginal ? "Original Sound" : s.title}
                        </div>
                        <div style={S.rowMeta}>
                          {[
                            s.artist,
                            s.duration ? formatDuration(s.duration) : null,
                            `${formatCount(count)} ${count === 1 ? "video" : "videos"}`,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleUse(s)}
                        style={S.rowUse}
                      >
                        Use
                      </button>
                    </div>
                  );
                })
              ) : (
                <div style={S.empty}>
                  {query ? "No sounds match your search." : "No sounds available yet."}
                </div>
              )}
            </div>
          </>
        ) : (
          /* =================== DETAIL MODE =================== */
          <>
            <div style={S.scroll}>
              <div style={S.hero}>
                <button
                  type="button"
                  onClick={() => togglePreview(sound)}
                  disabled={!sound.url}
                  style={S.discButton}
                  aria-label={
                    playingKey === getSoundKey(sound) ? "Pause" : "Play"
                  }
                >
                  <div
                    style={{
                      ...S.disc,
                      animation:
                        playingKey === getSoundKey(sound)
                          ? "sound-spin 6s linear infinite"
                          : "none",
                    }}
                  >
                    <MusicIcon size={38} color="#fff" />
                  </div>
                  <span style={S.discOverlay}>
                    {playingKey === getSoundKey(sound) ? (
                      <PauseIcon size={26} />
                    ) : (
                      <PlayIcon size={26} />
                    )}
                  </span>
                </button>

                <div style={S.heroInfo}>
                  <div style={S.heroTitle}>
                    {sound.isOriginal ? "Original Sound" : sound.title}
                  </div>
                  {sound.artist ? (
                    <div style={S.heroArtist}>{sound.artist}</div>
                  ) : null}
                  <div style={S.heroMeta}>
                    {[
                      sound.duration ? formatDuration(sound.duration) : null,
                      `${formatCount(usingPosts.length)} ${
                        usingPosts.length === 1 ? "video" : "videos"
                      }`,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </div>
              </div>

              <div style={S.sectionTitle}>Videos with this sound</div>

              {usingPosts.length ? (
                <div style={S.grid}>
                  {usingPosts.map((p) => (
                    <Tile key={p.postId} post={p} onClick={() => handleSelectPost(p)} />
                  ))}
                </div>
              ) : (
                <div style={S.empty}>
                  No videos yet — be the first to use this sound.
                </div>
              )}
            </div>

            <div style={S.footer}>
              <button
                type="button"
                onClick={() => handleUse(sound)}
                disabled={!sound.url}
                style={{ ...S.useButton, opacity: sound.url ? 1 : 0.5 }}
              >
                <MusicIcon size={18} color="#fff" />
                <span>{sound.url ? "Use this sound" : "Sound unavailable"}</span>
              </button>
            </div>
          </>
        )}
      </div>

      {/* Photo / Video chooser — shown on top of the Sound page after
          "Use this sound" */}
      {chooserSound ? (
        <div style={S.sheetBackdrop} onClick={() => setChooserSound(null)}>
          <div style={S.sheet} onClick={(e) => e.stopPropagation()}>
            <div style={S.sheetTitle}>Create with this sound</div>
            <button
              type="button"
              onClick={() => handleChooseKind("photo")}
              style={S.sheetButton}
            >
              <PhotoGlyph size={20} />
              <span>Photo</span>
            </button>
            <button
              type="button"
              onClick={() => handleChooseKind("video")}
              style={S.sheetButton}
            >
              <VideoGlyph size={20} />
              <span>Video</span>
            </button>
            <button
              type="button"
              onClick={() => setChooserSound(null)}
              style={S.sheetCancel}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {/* Single shared preview player */}
      <audio
        ref={audioRef}
        loop
        preload="none"
        onError={() => setPlayingKey(null)}
        style={{ display: "none" }}
      />
    </div>
  );
}

/* =========================================================
   🖼️ GRID TILE (one video that uses the sound)
========================================================= */

function Tile({ post, onClick }) {
  const m = mediaItemsOf(post)[0] || null;
  const thumb =
    m?.thumbnail || post?.coverThumbnail || (m?.type === "image" ? m.url : null);

  return (
    <button type="button" onClick={onClick} style={S.tile}>
      {thumb ? (
        <img src={thumb} alt="" style={S.tileMedia} />
      ) : m?.type === "video" && m?.url ? (
        <video
          src={`${m.url}#t=0.1`}
          preload="metadata"
          muted
          playsInline
          style={S.tileMedia}
        />
      ) : (
        <div style={S.tileMedia} />
      )}
      <span style={S.tileViews}>▶ {formatCount(post?.viewsCount)}</span>
    </button>
  );
}

/* =========================================================
   🎨 STYLES
========================================================= */

const S = {
  root: {
    background: "#0b0b0b",
    color: "#fff",
    fontFamily: "sans-serif",
    display: "flex",
    justifyContent: "center",
  },
  rootAbsolute: {
    position: "absolute",
    inset: 0,
    zIndex: 900,
  },
  rootFixed: {
    position: "fixed",
    inset: 0,
    zIndex: 1100, // above the preview sheet (z 1000) and the mobile tab bar
  },
  column: {
    width: "100%",
    maxWidth: 560,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },

  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 12px 8px",
    flex: "none",
  },
  headerTitle: { fontSize: 16, fontWeight: 700 },
  headerSpacer: { width: 40 },
  iconButton: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    border: "none",
    background: "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },

  scroll: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    touchAction: "pan-y",
    WebkitOverflowScrolling: "touch",
    padding: "0 16px 16px",
  },

  /* detail */
  hero: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: "12px 0 20px",
  },
  discButton: {
    position: "relative",
    flex: "none",
    width: 112,
    height: 112,
    padding: 0,
    border: "none",
    background: "transparent",
    cursor: "pointer",
  },
  disc: {
    width: 112,
    height: 112,
    borderRadius: "50%",
    background:
      "radial-gradient(circle at 50% 50%, #2b2b2b 0 22%, #111 23% 100%)",
    boxShadow: "0 0 0 3px #1d9bf0 inset",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  discOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(0,0,0,0.35)",
    borderRadius: "50%",
  },
  heroInfo: { minWidth: 0, flex: 1 },
  heroTitle: { fontSize: 20, fontWeight: 800, lineHeight: 1.2 },
  heroArtist: { fontSize: 14, color: "#c8c8c8", marginTop: 4 },
  heroMeta: { fontSize: 12, color: "#8e8e8e", marginTop: 6 },

  sectionTitle: {
    fontSize: 14,
    fontWeight: 700,
    margin: "4px 0 10px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 3,
  },
  tile: {
    position: "relative",
    padding: 0,
    border: "none",
    background: "#1a1a1a",
    aspectRatio: "3 / 4",
    overflow: "hidden",
    cursor: "pointer",
  },
  tileMedia: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    background: "#1a1a1a",
  },
  tileViews: {
    position: "absolute",
    left: 6,
    bottom: 5,
    fontSize: 11,
    fontWeight: 600,
    color: "#fff",
    textShadow: "0 1px 3px rgba(0,0,0,0.8)",
  },

  sheetBackdrop: {
    position: "absolute",
    inset: 0,
    zIndex: 5,
    background: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
  },
  sheet: {
    width: "100%",
    maxWidth: 560,
    background: "#161616",
    borderTopLeftRadius: 18,
    borderTopRightRadius: 18,
    padding: "16px 16px calc(16px + var(--moment-inset, 0px))",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  sheetTitle: {
    textAlign: "center",
    fontSize: 14,
    fontWeight: 700,
    color: "#cfcfcf",
    marginBottom: 4,
  },
  sheetButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    padding: "14px 0",
    borderRadius: 14,
    border: "none",
    background: "#232323",
    color: "#fff",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  },
  sheetCancel: {
    padding: "12px 0",
    border: "none",
    background: "transparent",
    color: "#9a9a9a",
    fontSize: 14,
    cursor: "pointer",
  },
  footer: {
    flex: "none",
    padding: "10px 16px",
    // lifts the button clear of the mobile bottom tab bar (set by Moment)
    paddingBottom: "calc(12px + var(--moment-inset, 0px))",
    borderTop: "1px solid #1f1f1f",
    background: "#0b0b0b",
  },
  useButton: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "13px 0",
    borderRadius: 26,
    border: "none",
    background: "#1d9bf0",
    color: "#fff",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
  },

  /* pick */
  searchWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    margin: "0 16px 8px",
    padding: "0 14px",
    height: 40,
    borderRadius: 20,
    background: "#1c1c1c",
    flex: "none",
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    background: "transparent",
    border: "none",
    outline: "none",
    color: "#fff",
    fontSize: 14,
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 0",
    borderBottom: "1px solid #181818",
  },
  rowPlay: {
    flex: "none",
    width: 44,
    height: 44,
    borderRadius: "50%",
    border: "none",
    background: "#222",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  rowInfo: { flex: 1, minWidth: 0 },
  rowTitle: {
    fontSize: 14,
    fontWeight: 700,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  rowMeta: {
    fontSize: 12,
    color: "#8e8e8e",
    marginTop: 2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  rowUse: {
    flex: "none",
    padding: "7px 16px",
    borderRadius: 18,
    border: "none",
    background: "#1d9bf0",
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  },

  empty: {
    padding: "36px 8px",
    textAlign: "center",
    fontSize: 13,
    color: "#8e8e8e",
  },
};

/* =========================================================
   🌀 KEYFRAMES (spinning disc)
   Injected once — safe if the module is evaluated multiple times.
========================================================= */

if (
  typeof document !== "undefined" &&
  !document.getElementById("sound-keyframes")
) {
  const styleTag = document.createElement("style");
  styleTag.id = "sound-keyframes";
  styleTag.innerHTML = `
    @keyframes sound-spin {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(styleTag);
}

export default Sound;